"use client";

/**
 * Consume a better-auth one-time token from the iOS app handoff.
 * Turns the token into a cookie session so checkout sees the signed-in user.
 */
export async function consumeOneTimeToken(
	token: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
	const trimmed = token.trim();
	if (!trimmed) {
		return { ok: false, error: "Missing token" };
	}

	try {
		const res = await fetch("/api/auth/one-time-token/verify", {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ token: trimmed }),
		});
		if (!res.ok) {
			const payload = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
			return {
				ok: false,
				error: payload.message || payload.error || "Could not restore your session.",
			};
		}
		return { ok: true };
	} catch {
		return { ok: false, error: "Could not restore your session." };
	}
}

/**
 * Generate a one-time token for the current cookie/bearer session.
 * Used on the welcome page to hand the user back to the iOS app.
 */
export async function generateOneTimeToken(): Promise<
	{ ok: true; token: string } | { ok: false; error: string }
> {
	try {
		const res = await fetch("/api/auth/one-time-token/generate", {
			method: "GET",
			credentials: "include",
		});
		const payload = (await res.json().catch(() => ({}))) as {
			token?: string;
			message?: string;
			error?: string;
		};
		if (!res.ok || !payload.token) {
			return {
				ok: false,
				error: payload.message || payload.error || "Could not prepare app handoff.",
			};
		}
		return { ok: true, token: payload.token };
	} catch {
		return { ok: false, error: "Could not prepare app handoff." };
	}
}

export const IOS_APP_CALLBACK_SCHEME = "unilytics";
export const IOS_APP_AUTH_CALLBACK_URL = `${IOS_APP_CALLBACK_SCHEME}://auth/callback`;

export function buildIosAuthCallbackUrl(token: string): string {
	const url = new URL(IOS_APP_AUTH_CALLBACK_URL);
	url.searchParams.set("token", token);
	return url.toString();
}
